import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { useDeleteService } from '@/api/service-settings';
import { trackFeatureAction } from '@/lib/analytics-events';

/** Pull a human message out of an axios-style error, with a sensible fallback. */
function errorMessage(e: unknown, fallback: string): string {
  const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
  return msg || fallback;
}

/**
 * Destructive confirm for deleting a Compute service. Mirrors
 * WaapDeleteServiceDialog: type-the-name friction, blocked while instances are
 * still running, recorded in the audit log.
 */
export function ComputeDeleteServiceDialog({
  open,
  onOpenChange,
  serviceId,
  serviceName,
  instanceCount,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  serviceId: string;
  serviceName: string;
  instanceCount: number;
}) {
  const { show } = useToast();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState('');
  const del = useDeleteService(serviceId);

  useEffect(() => {
    if (open) setConfirm('');
  }, [open]);

  const blocked = instanceCount > 0;
  const nameMatches = confirm.trim() === serviceName.trim();

  const submit = () => {
    if (blocked || !nameMatches || del.isPending) return;
    del.mutate(undefined, {
      onSuccess: (res) => {
        trackFeatureAction('compute', 'service', 'delete');
        show({ tone: 'success', title: `Deleted ${res?.service_name ?? serviceName}` });
        onOpenChange(false);
        navigate('/services');
      },
      onError: (e) =>
        show({
          tone: 'danger',
          title: 'Could not delete service',
          description: errorMessage(e, 'Deletion failed — the service was left untouched.'),
        }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete "{serviceName}"</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            {blocked ? (
              <Alert tone="warning" title="This service still has instances">
                {instanceCount} instance{instanceCount === 1 ? '' : 's'} are still running.
                Delete them first — deletion is only possible for an empty service.
              </Alert>
            ) : (
              <Alert tone="danger" title="This deletes the whole service">
                The Compute service and its zone bindings are removed from your
                console. The action is recorded in the activity log.
              </Alert>
            )}
            {!blocked && (
              <>
                <p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                  Type <strong>{serviceName}</strong> to confirm.
                </p>
                <Input
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                  placeholder={serviceName}
                  autoFocus
                  aria-label="Type the service name to confirm deletion"
                />
              </>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            variant="danger"
            disabled={blocked || !nameMatches || del.isPending}
            onClick={submit}
          >
            {del.isPending ? 'Deleting…' : 'Delete service'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
